import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { ProductDbService } from '../../../services/productdb.service';
import {
  HoldReceiptInfo,
  TransactionHttpResponse,
  TransactionsHttpResponse,
} from '../../../modals/product.modals';
import { RestoredModeState } from '../models/hold-receipt.model';

/**
 * ResumeReceiptService
 *
 * Single Responsibility: owns all read-side operations for held orders.
 *
 * Methods extracted from dashboard.component.ts:
 *  - getHoldReceipts()         ← getAllTransaction(true)  L2436–2458
 *  - getHoldOrderDetail()      ← tapSpecificHoldOrder()   L5327
 *  - restoreModesFromGroupId() ← getSpecificTransaction() L5356–5380
 *
 * None of these methods mutate component state — they return typed values.
 */
@Injectable({ providedIn: 'root' })
export class ResumeReceiptService {
  constructor(private readonly productDbService: ProductDbService) {}

  /**
   * Returns only held orders (status === 'Hold') mapped to HoldReceiptInfo[].
   *
   * Replaces the confusing dual-mode flag in getAllTransaction(showSavedTransaction=true).
   */
  getHoldReceipts(): Observable<HoldReceiptInfo[]> {
    return this.productDbService.getHoldTransaction().pipe(
      map((response: TransactionsHttpResponse) => {
        if (response.code !== '200' || !response.data) {
          return [];
        }
        return response.data
          .filter((item) => item.status === 'Hold')
          .map(
            (item): HoldReceiptInfo => ({
              uuid: item.uuid,
              tempname: item.tempname,
              amount: item.amount,
              updatedAt: item.updatedAt,
            })
          );
      })
    );
  }

  /**
   * Fetches the full detail of a specific held order so the cart can be restored.
   * Thin delegation to ProductDbService.tapSpecificHoldOrder().
   */
  getHoldOrderDetail(uuid: string): Observable<TransactionHttpResponse> {
    return this.productDbService.tapSpecificHoldOrder(uuid);
  }

  /**
   * Pure function: resolves which trading mode was active when the order was held,
   * based on the groupId stored on the first cart item.
   *
   * groupId meanings (same convention used by the backend):
   *   -2              → Suspense mode
   *   -1              → Wholesale mode
   *   > 0             → Offer-group mode (groupId is the group's numeric id)
   *   0 / null / undef → Normal retail mode
   */
  restoreModesFromGroupId(
    groupId: number | undefined | null
  ): RestoredModeState {
    if (groupId === -2) {
      return {
        isWholesaleMode: false,
        isSuspenseMode: true,
        isOfferGroupMode: false,
        selectedOfferGroupId: null,
      };
    }

    if (groupId === -1) {
      return {
        isWholesaleMode: true,
        isSuspenseMode: false,
        isOfferGroupMode: false,
        selectedOfferGroupId: null,
      };
    }

    if (groupId !== undefined && groupId !== null && groupId > 0) {
      return {
        isWholesaleMode: false,
        isSuspenseMode: false,
        isOfferGroupMode: true,
        selectedOfferGroupId: groupId,
      };
    }

    // Normal retail or unrecognised value
    return {
      isWholesaleMode: false,
      isSuspenseMode: false,
      isOfferGroupMode: false,
      selectedOfferGroupId: null,
    };
  }
}
